/**
 * Byteline-hosted images.
 *
 * Publisher pictures are copied into our own storage by the mirroring job and
 * served from /img/<hash>/<width>.webp on this domain. Nothing is requested
 * from an outside image host at render time.
 */

import { imageHash } from "./imageHash";

/** Rendition widths we store. Requests snap up to the nearest one. */
export const IMAGE_WIDTHS = [64, 96, 128, 160, 320, 480, 560, 700, 960] as const;
const MAX_WIDTH = IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];

export interface CdnImageOptions {
  /** Rendered CSS width in px. */
  width?: number;
  /** Rendered CSS height in px (kept for call-site compatibility). */
  height?: number;
  /** Device pixel ratio multiplier (capped at 2). */
  dpr?: number;
  /** Ignored: renditions are encoded once at a fixed quality. */
  quality?: number;
  /** Ignored: renditions keep the original aspect ratio; CSS handles fit. */
  fit?: "cover" | "contain" | "inside" | "outside" | "fill";
  /** Stored formats. WebP is always available; AVIF when it was encoded. */
  format?: "webp" | "avif" | "jpg" | "png";
}

/** URLs we must never rewrite (local assets, data URIs, SVG, already ours). */
export function shouldProxy(url?: string | null): url is string {
  if (!url) return false;
  const u = url.trim();
  if (!u) return false;
  if (u.startsWith("data:") || u.startsWith("blob:")) return false;
  if (u.startsWith("/img/")) return false;
  if (!/^https?:\/\//i.test(u)) return false; // relative/local -> already ours
  if (/\.svg(\?|#|$)/i.test(u)) return false;
  return true;
}

/** Pick the stored rendition that covers the requested width. */
export function snapWidth(width?: number, dpr = 1): number {
  const scale = Math.min(Math.max(dpr, 1), 2);
  const target = width ? Math.round(width * scale) : MAX_WIDTH;
  return IMAGE_WIDTHS.find((w) => w >= target) ?? MAX_WIDTH;
}

/**
 * Version marker in the path. Bump it whenever the stored renditions are
 * re-encoded, so long-lived caches fetch the new (smaller) files.
 */
export const IMAGE_VERSION = "v3";

/** Byteline path for a stored rendition of a remote picture. */
export function cdnImage(url?: string | null, options: CdnImageOptions = {}): string {
  if (!shouldProxy(url)) return url || "";
  const width = snapWidth(options.width, options.dpr ?? 1);
  const ext = options.format === "avif" ? "avif" : "webp";
  return `/img/${IMAGE_VERSION}/${imageHash(url)}/${width}.${ext}`;
}

/** Build a `1x, 2x` srcSet for a fixed-size image box. */
export function cdnSrcSet(url?: string | null, options: CdnImageOptions = {}): string | undefined {
  if (!shouldProxy(url)) return undefined;
  const one = cdnImage(url, { ...options, dpr: 1 });
  const two = cdnImage(url, { ...options, dpr: 2 });
  if (one === two) return undefined;
  return `${one} 1x, ${two} 2x`;
}

/** Rewrite every <img src> inside an HTML string (article bodies). */
export function proxyHtmlImages(html: string, width = 960): string {
  return html.replace(/<img([^>]*?)src=["']([^"']+)["']/gi, (match, attrs, src) => {
    if (!shouldProxy(src)) return match;
    return `<img${attrs}src="${cdnImage(src, { width })}"`;
  });
}

/**
 * Width-descriptor srcSet (`... 320w, ... 700w`) so the browser picks the
 * smallest stored rendition that fits its layout box.
 */
export function cdnWidthSet(
  url?: string | null,
  widths: number[] = [...IMAGE_WIDTHS],
  options: CdnImageOptions & { maxWidth?: number } = {}
): string | undefined {
  if (!shouldProxy(url)) return undefined;
  const cap = options.maxWidth ?? MAX_WIDTH;
  const snapped = Array.from(new Set(widths.map((w) => snapWidth(w))))
    .filter((w) => w <= cap)
    .sort((a, b) => a - b);
  if (snapped.length === 0) snapped.push(snapWidth(cap));
  const hash = imageHash(url);
  const ext = options.format === "avif" ? "avif" : "webp";
  return snapped.map((w) => `/img/${IMAGE_VERSION}/${hash}/${w}.${ext} ${w}w`).join(", ");
}

/** Same as cdnWidthSet, but pointing at the AVIF copies. */
export function cdnAvifSet(
  url?: string | null,
  widths: number[] = [...IMAGE_WIDTHS],
  options: CdnImageOptions & { maxWidth?: number } = {}
): string | undefined {
  return cdnWidthSet(url, widths, { ...options, format: "avif" });
}

/** `1x, 2x` AVIF srcSet for fixed-size boxes. */
export function cdnAvifSrcSet(url?: string | null, options: CdnImageOptions = {}): string | undefined {
  return cdnSrcSet(url, { ...options, format: "avif" });
}
